import React, { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import { formatSowDateRange, isSowActiveForDate } from './sowOutlineUtils';
import type { SOWOutline } from './ClientOutlineTypes';

interface SOWSelectorProps {
    sows: SOWOutline[];
    selectedSow: SOWOutline;
    today: Date;
    onSelect: ( sowId: string ) => void;
}

interface SowGroup {
    key: 'active' | 'inactive';
    label: string;
    sows: SOWOutline[];
}

// Below this, the full list fits at a glance and a search box is just noise.
const SEARCH_THRESHOLD = 6;

const matchesQuery = ( sow: SOWOutline, query: string ): boolean => {
    if( !query ) return true;
    const haystack = `${ sow.SOWName } ${ sow.SowLeadConsultant ?? '' }`.toLowerCase();
    return haystack.includes( query );
};

const SOWSelector: FC<SOWSelectorProps> = ( { sows, selectedSow, today, onSelect } ) => {
    const [ isOpen, setIsOpen ]               = useState( false ),
          [ query, setQuery ]                 = useState( '' ),
          [ highlightedId, setHighlightedId ] = useState( '' ),
          searchRef                           = useRef<HTMLInputElement>( null ),
          listRef                             = useRef<HTMLDivElement>( null );

    const showSearch = sows.length >= SEARCH_THRESHOLD;
    const normalizedQuery = query.trim().toLowerCase();
    const isSelectedActive = isSowActiveForDate( selectedSow, today );

    const groups = useMemo<SowGroup[]>( () => {
        const matching = sows.filter( sow => matchesQuery( sow, normalizedQuery ) );
        return [
            { key: 'active' as const, label: 'Active', sows: matching.filter( sow => isSowActiveForDate( sow, today ) ) },
            { key: 'inactive' as const, label: 'Inactive', sows: matching.filter( sow => !isSowActiveForDate( sow, today ) ) },
        ].filter( group => group.sows.length > 0 );
    }, [ sows, normalizedQuery, today ] );

    const flatOptions = useMemo(
        () => groups.reduce<SOWOutline[]>( ( all, group ) => all.concat( group.sows ), [] ),
        [ groups ]
    );

    useEffect( () => {
        if( !isOpen ) return;
        setQuery( '' );
        setHighlightedId( selectedSow.SOWId );
        // Popup content mounts after open; wait a frame before focusing.
        const frameId = window.requestAnimationFrame( () => {
            if( showSearch ) searchRef.current?.focus();
            else listRef.current?.focus();
        } );
        return () => window.cancelAnimationFrame( frameId );
    }, [ isOpen, selectedSow.SOWId, showSearch ] );

    useEffect( () => {
        if( !isOpen || !highlightedId ) return;
        const option = listRef.current?.querySelector<HTMLElement>( `[data-sow-id="${ highlightedId }"]` );
        option?.scrollIntoView( { block: 'nearest' } );
    }, [ isOpen, highlightedId ] );

    useEffect( () => {
        if( flatOptions.length === 0 ) return;
        if( !flatOptions.some( sow => sow.SOWId === highlightedId ) ){
            setHighlightedId( flatOptions[ 0 ].SOWId );
        }
    }, [ flatOptions, highlightedId ] );

    const handleSelect = ( sowId: string ) => {
        onSelect( sowId );
        setIsOpen( false );
    };

    const moveHighlight = ( step: number ) => {
        if( flatOptions.length === 0 ) return;
        const currentIndex = flatOptions.findIndex( sow => sow.SOWId === highlightedId );
        const nextIndex = currentIndex === -1
            ? 0
            : ( currentIndex + step + flatOptions.length ) % flatOptions.length;
        setHighlightedId( flatOptions[ nextIndex ].SOWId );
    };

    const handleKeyDown = ( event: KeyboardEvent<HTMLElement> ) => {
        if( event.key === 'ArrowDown' ){
            event.preventDefault();
            moveHighlight( 1 );
        } else if( event.key === 'ArrowUp' ){
            event.preventDefault();
            moveHighlight( -1 );
        } else if( event.key === 'Enter' ){
            event.preventDefault();
            if( highlightedId ) handleSelect( highlightedId );
        } else if( event.key === 'Escape' ){
            event.preventDefault();
            setIsOpen( false );
        }
    };

    const listContent = (
        <div className={ 'sow-selector-menu' }>
            { showSearch && (
                <div className={ 'sow-selector-search' }>
                    <Icon name={ 'search' } className={ 'sow-selector-search-icon' } />
                    <input
                        ref={ searchRef }
                        type={ 'text' }
                        value={ query }
                        placeholder={ 'Search SOWs or lead consultants' }
                        aria-label={ 'Search SOWs' }
                        onChange={ event => setQuery( event.target.value ) }
                        onKeyDown={ handleKeyDown }
                    />
                </div>
            ) }

            <div
                ref={ listRef }
                role={ 'listbox' }
                aria-label={ 'Statements of work' }
                aria-activedescendant={ highlightedId ? `sow-option-${ highlightedId }` : undefined }
                tabIndex={ showSearch ? -1 : 0 }
                className={ 'sow-selector-list' }
                onKeyDown={ showSearch ? undefined : handleKeyDown }
            >
                { groups.length === 0 && (
                    <p className={ 'sow-selector-empty' }>No SOWs match &ldquo;{ query.trim() }&rdquo;.</p>
                ) }

                { groups.map( group => (
                    <div key={ group.key } role={ 'group' } aria-label={ group.label } className={ 'sow-selector-group' }>
                        <div className={ 'sow-selector-group-label' }>
                            { group.label }
                            <span className={ 'sow-selector-group-count' }>{ group.sows.length }</span>
                        </div>

                        { group.sows.map( sow => {
                            const isSelected = sow.SOWId === selectedSow.SOWId;
                            const isHighlighted = sow.SOWId === highlightedId;
                            const lead = sow.SowLeadConsultant?.trim();

                            return (
                                <div
                                    key={ sow.SOWId }
                                    id={ `sow-option-${ sow.SOWId }` }
                                    data-sow-id={ sow.SOWId }
                                    role={ 'option' }
                                    aria-selected={ isSelected }
                                    className={ `sow-selector-option${ isSelected ? ' is-selected' : '' }${ isHighlighted ? ' is-highlighted' : '' }` }
                                    onMouseEnter={ () => setHighlightedId( sow.SOWId ) }
                                    onClick={ () => handleSelect( sow.SOWId ) }
                                >
                                    <span className={ `sow-selector-dot is-${ group.key }` } aria-hidden />
                                    <span className={ 'sow-selector-option-main' }>
                                        <span className={ 'sow-selector-option-name' }>{ sow.SOWName }</span>
                                        <span className={ 'sow-selector-option-meta' }>
                                            { formatSowDateRange( sow ) }
                                            { lead ? ` · ${ lead }` : '' }
                                        </span>
                                    </span>
                                    { sow.ExpansionOpportunity === true && (
                                        <Icon
                                            name={ 'chart line' }
                                            className={ 'sow-selector-option-expansion' }
                                            title={ 'Expansion opportunity' }
                                        />
                                    ) }
                                    { isSelected && <Icon name={ 'check' } className={ 'sow-selector-option-check' } /> }
                                </div>
                            );
                        } ) }
                    </div>
                ) ) }
            </div>
        </div>
    );

    return (
        <Popup
            on={ 'click' }
            position={ 'bottom left' }
            basic
            open={ isOpen }
            onOpen={ () => setIsOpen( true ) }
            onClose={ () => setIsOpen( false ) }
            className={ 'sow-selector-popup' }
            content={ listContent }
            trigger={
                <button
                    type={ 'button' }
                    className={ `sow-selector-trigger${ isOpen ? ' is-open' : '' }` }
                    aria-haspopup={ 'listbox' }
                    aria-expanded={ isOpen }
                    aria-label={ `Statement of work: ${ selectedSow.SOWName }. Change SOW` }
                >
                    <span className={ `sow-selector-dot is-${ isSelectedActive ? 'active' : 'inactive' }` } aria-hidden />
                    <span className={ 'sow-selector-trigger-name' }>{ selectedSow.SOWName }</span>
                    <span className={ 'sow-selector-trigger-meta' }>
                        { isSelectedActive ? 'Active' : 'Inactive' }
                    </span>
                    <Icon name={ 'chevron down' } className={ 'sow-selector-chevron' } />
                </button>
            }
        />
    );
};

export default SOWSelector;
